import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { isSessionExpired } from '../../shared/services/userSessionService';

export interface SessionState {
  // identity
  userId: number;
  username: string;
  emis_username: string;
  name: string;
  teacher_name: string;
  Socialname: string;
  designation: string;
  phnumber: string;
  email: string;

  // school
  schoolName: string;
  schoolId: number;
  schoolKeyId: string;
  schoolTypeId: number;
  schlId: string;
  highClass: number;
  lowClass: number;

  // teacher
  teacherId: number;
  teacherType: string;
  type_teacher: string;
  subjects: string;
  subject_id: string;

  // user type / role
  usertype: string;
  usertypeid: string;
  emisUsertype1: string;
  roleId: number;
  offKeyId: string;

  // category
  cate_id: string;
  catty_id: string;
  cateType: string;
  category_type: string;
  manage_id: string;

  // location
  districtId: string;
  districtName: string;
  blockId: string;
  blockName: string;
  edu_dist_name: string;
  edn_dist_name: string;
  edu_district_id: string;

  // module permissions
  mod2: string;

  // switch login
  switch_login: string;
  switch_designation: string;

  // token / meta
  authToken: string;
  iat: number;
  tokenExpiry: number;

  loaded: boolean;
}

// ── helpers ──────────────────────────────────────────────────────────────────

const str = (key: string): string => localStorage.getItem(key) ?? '';
const num = (key: string): number => Number(localStorage.getItem(key) ?? 0);

/** Read Angular-written localStorage keys back into Redux on every page load */
function hydrateFromStorage(): SessionState {
  const token = str('token');
  const isBridgeSynced = localStorage.getItem('AUTH_BRIDGE_SYNC') === 'true';

  // Case 2: no token at all — not logged in
  if (!token && !isBridgeSynced) {
    return { ...blankState, loaded: false };
  }

  // Case 1: token exists but session is expired (tab closed without logout,
  // or SESSION_EXTENDED_UNTIL has also lapsed) — wipe storage and force login
  if (isSessionExpired()) {
    localStorage.clear();
    return { ...blankState, loaded: false };
  }

  return {
    // identity
    userId: num('userId'),
    username: str('username'),
    emis_username: str('emis_username'),
    name: str('name'),
    teacher_name: str('teacher_name'),
    Socialname: str('Socialname'),
    designation: str('designation'),
    phnumber: str('phnumber'),
    email: str('email'),

    // school
    schoolName: str('schoolName'),
    schoolId: num('schoolId'),
    schoolKeyId: str('schoolKeyId'),
    schoolTypeId: num('schoolTypeId'),
    schlId: str('schlId'),
    highClass: num('highClass'),
    lowClass: num('lowClass'),

    // teacher
    teacherId: num('teacherId'),
    teacherType: str('teacherType'),
    type_teacher: str('type_teacher'),
    subjects: str('subjects'),
    subject_id: str('subject_id'),

    // user type / role
    usertype: str('usertype'),
    usertypeid: str('usertypeid'),
    emisUsertype1: str('emisUsertype1'),
    roleId: num('roleId'),
    offKeyId: str('offKeyId'),

    // category
    cate_id: str('cate_id'),
    catty_id: str('catty_id'),
    cateType: str('cateType'),
    category_type: str('category_type'),
    manage_id: str('manage_id'),

    // location
    districtId: str('districtId'),
    districtName: str('districtName'),
    blockId: str('blockId'),
    blockName: str('blockName'),
    edu_dist_name: str('edu_dist_name'),
    edn_dist_name: str('edn_dist_name'),
    edu_district_id: str('edu_district_id'),

    // module permissions
    mod2: str('mod2'),

    // switch login
    switch_login: str('switch_login'),
    switch_designation: str('switch_designation'),

    // token / meta
    authToken: str('token'), // Angular writes 'token', map it here
    iat: num('iat'),
    tokenExpiry: num('tokenExpiry'),

    loaded: true, // ← marks session as ready, prevents login redirect
  };
}

const blankState: SessionState = {
  userId: 0,
  username: '',
  emis_username: '',
  name: '',
  teacher_name: '',
  Socialname: '',
  designation: '',
  phnumber: '',
  email: '',
  schoolName: '',
  schoolId: 0,
  schoolKeyId: '',
  schoolTypeId: 0,
  schlId: '',
  highClass: 0,
  lowClass: 0,
  teacherId: 0,
  teacherType: '',
  type_teacher: '',
  subjects: '',
  subject_id: '',
  usertype: '',
  usertypeid: '',
  emisUsertype1: '',
  roleId: 0,
  offKeyId: '',
  cate_id: '',
  catty_id: '',
  cateType: '',
  category_type: '',
  manage_id: '',
  districtId: '',
  districtName: '',
  blockId: '',
  blockName: '',
  edu_dist_name: '',
  edn_dist_name: '',
  edu_district_id: '',
  mod2: '',
  switch_login: '',
  switch_designation: '',
  authToken: '',
  iat: 0,
  tokenExpiry: 0,
  loaded: false,
};

// ── slice ─────────────────────────────────────────────────────────────────────

const sessionSlice = createSlice({
  name: 'session',
  initialState: hydrateFromStorage, // ← function ref, RTK calls it lazily
  reducers: {
    loadSession(_state, action: PayloadAction<Omit<SessionState, 'loaded'>>) {
      return { ...action.payload, loaded: true };
    },
    clearSession() {
      Object.keys(localStorage).forEach((key) => {
        console.log('key is', key);
        if (key !== 'AuthorizationKey') {
          localStorage.removeItem(key);
        }
      });
      return blankState;
    },
  },
});

export const { loadSession, clearSession } = sessionSlice.actions;
export default sessionSlice.reducer;
